<template>
  <v-card class="secondary" height="100%">
    <v-card-title class="font-weight-medium secondary--text primary">
      {{ edit ? `Edit Settlement` : "Add Settlement" }}
      <v-spacer></v-spacer>
      <v-btn small icon @click="$emit('closeDialog')">
        <v-icon small color="secondary">mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-form ref="form">
    <!-- Step 1: Settlement Summary -->
    <v-card-text v-if="step === 1" class="pt-5">
      <v-row>
        <v-col cols="4">
          <v-menu
            v-model="showDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.date"
                label="Date*"
                append-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="form.date"
              @input="showDatePicker = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <!-- Other fields -->
        <v-col cols="4">
          <v-text-field
            v-model="form.pv_number"
            label="PV Number*"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.net_credit"
            label="Net Credit*"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.total_gross_amount"
            label="Total Gross Amount*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.total_charge_back_amount"
            label="Total Charge Back Amount*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.total_refund_amount"
            label="Total Refund Amount*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.escrow_tx_amount"
            label="Escrow Tx Amount*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.charges"
            label="Charges*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.gst"
            label="GST*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model.number="form.settled_amount"
            label="Settled Amount*"
            hide-details="auto"
            outlined
            dense
            :rules="[rules.number, rules.acceptZero]"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="d-flex justify-end">
          <v-btn class="primary" @click="validateSettlement">Next</v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Step 2: Settled Transactions -->
    <v-card-text v-if="step === 2" class="pt-5">
      <div class="d-flex justify-space-between">
        <h4 class="mt-0">Settled Transactions</h4>
        <v-spacer></v-spacer>
        <v-btn
          small
          dense
          text
          class="mt-0"
          @click="
            settled_transaction_mode === 'add'
              ? viewTransaction()
              : addViewTransaction()
          "
        >
          {{ settled_transaction_mode === "add" ? "view" : "add" }}
        </v-btn>
      </div>
      <v-form ref="transactionForm" v-if="settled_transaction_mode === 'add'">
        <v-row class="mt-2">
          <v-col cols="4"
            ><v-text-field
              v-model="transaction.pgi_ref_number"
              label="PGI Ref Number"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model="transaction.ref1"
              label="Ref Number"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model="transaction.tx_date"
              label="Transaction Date"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model="transaction.settlement_date"
              label="Settlement Date"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model.number="transaction.gross_amount"
              label="Gross Amount"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.number, rules.acceptZero]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model.number="transaction.charges"
              label="Charges"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.number, rules.acceptZero]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model.number="transaction.gst"
              label="GST"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.number, rules.acceptZero]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model.number="transaction.net_amount"
              label="Net Amount"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.number, rules.acceptZero]"
          /></v-col>
          <v-col cols="4"
            ><v-text-field
              v-model="transaction.pay_ref"
              label="Pay Ref"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
          /></v-col>
        </v-row>
      </v-form>

      <v-data-table
        v-else
        :headers="headers"
        :items="form.settled_transactions"
        hide-default-footer
        class="elevation-1 mt-2"
      >
        <template #[`item.action`]="{ item }">
          <v-icon
            small
            class="mr-2"
            color="accent"
            @click="editSettledTransaction(item)"
          >
            mdi-pencil
          </v-icon>
        </template></v-data-table
      >

      <v-row class="mt-3">
        <v-col cols="6">
          <v-btn text @click="step = 1">Back</v-btn>
        </v-col>
        <v-col cols="6" class="text-right">
          <v-btn
            class="primary mr-2"
            v-if="settled_transaction_mode === 'add'"
            @click="addTransaction"
            >Add Transaction</v-btn
          >

          <v-btn
            class="primary mr-2"
            v-if="settled_transaction_mode === 'edit'"
            @click="updateTransaction"
            >Update Transaction</v-btn
          >
          <v-btn class="primary" @click="save">{{
            edit ? "Update" : "Save"
          }}</v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    settlement: { type: Object, default: () => ({}) },
    edit: { type: Boolean, default: () => false },
  },
  data() {
    return {
      step: 1,
      rules,
      form: {
        date: "",
        pv_number: "",
        net_credit: 0,
        total_gross_amount: 0,
        total_charge_back_amount: 0,
        total_refund_amount: 0,
        escrow_tx_amount: 0,
        charges: 0,
        gst: 0,
        settled_amount: 0,
        settled_transactions: [],
        refund_transactions: [],
        charge_backs: [],
      },
      transaction: {
        pgi_ref_number: "",
        ref1: "",
        tx_date: "",
        settlement_date: "",
        gross_amount: 0,
        charges: 0,
        gst: 0,
        net_amount: 0,
        pay_ref: "",
      },
      headers: [
        { text: "PGI Ref Number", value: "pgi_ref_number" },
        { text: "Ref1", value: "ref1" },
        { text: "Tx Date", value: "tx_date" },
        { text: "Settlement Date", value: "settlement_date" },
        { text: "Gross Amount", value: "gross_amount" },
        { text: "Charges", value: "charges" },
        { text: "GST", value: "gst" },
        { text: "Net Amount", value: "net_amount" },
        { text: "Pay Ref", value: "pay_ref" },
        { text: "Action", value: "action", sortable: false },
      ],
      showDatePicker: false,
      settled_transaction_mode: "add",
    };
  },
  created() {
    if (this.edit && this.settlement) {
      this.form = { ...this.settlement };
      this.form.settled_transaction_mode = "view";
    }
  },
  methods: {
    addTransaction() {
      if (!this.$refs.transactionForm.validate()) return;
      this.form.settled_transactions.push({ ...this.transaction });
      this.settled_transaction_mode = "view";
      this.resetTransaction();
    },
    editSettledTransaction(item) {
      this.transaction = { ...item };
      this.editIndex = this.form.settled_transactions.indexOf(item);
      this.settled_transaction_mode = "edit";
      // this.$refs.transactionForm.resetValidation();
    },
    updateTransaction() {
      if (!this.$refs.transactionForm.validate()) return;
      this.$set(this.form.settled_transactions, this.editIndex, {
        ...this.transaction,
      });
      this.resetTransaction();
      this.settled_transaction_mode = "view";
    },
    resetTransaction() {
      this.transaction = {
        pgi_ref_number: "",
        ref1: "",
        tx_date: "",
        settlement_date: "",
        gross_amount: 0,
        charges: 0,
        gst: 0,
        net_amount: 0,
        pay_ref: "",
      };
      this.editIndex = null;
    },
    validateSettlement() {
      this.step = 2;
    },
    viewTransaction() {
      this.settled_transaction_mode = "view";
    },
    addViewTransaction() {
      this.settled_transaction_mode = "add";
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.$emit(this.edit ? "update" : "create", this.form);
    },
  },
};
</script>

